import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { SITE } from "./siteData";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-black"
    >
      <motion.div style={{ scale: videoScale }} className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={SITE.hero}
          data-testid="hero-video"
        >
          <source src="/hero-bg.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-black/20" />
        <div className="absolute inset-0 bg-black/20" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24"
      >
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="text-gold font-semibold uppercase tracking-[0.25em] text-sm">
              {SITE.city}
            </span>
          </div>

          <h1 className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-white leading-[0.95] tracking-tight">
            Build Your Best <span className="text-gold">Body</span> At SVT Golden Fitness
          </h1>

          <p className="mt-6 text-lg text-zinc-300 max-w-xl font-body">
            Bangalore's friendly fitness home. Strong trainers, modern gear and a crowd that
            keeps you going. Start today, feel better tomorrow.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="hero-book-trial-cta"
              className="bg-gold hover:bg-gold_dark text-black font-bold uppercase tracking-wider px-8 py-4 rounded-md transition-all hover:scale-[1.03] flex items-center justify-center gap-2"
            >
              Book Free Trial <ArrowRight size={18} />
            </a>
            <a
              href="#programs"
              data-testid="hero-explore-cta"
              className="border border-white/30 hover:border-gold text-white hover:text-gold font-bold uppercase tracking-wider px-8 py-4 rounded-md transition-all flex items-center justify-center gap-2"
            >
              See Programs
            </a>
          </div>

          <div className="mt-12 flex items-center gap-6">
            <div className="flex items-center gap-1 text-gold">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} fill="currentColor" />
              ))}
            </div>
            <p className="text-zinc-300 text-sm font-body">
              Trusted by <span className="text-white font-semibold">500+</span> happy members
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-zinc-400 text-xs uppercase tracking-[0.3em]">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
};
